import React, { useRef } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, TextInput as RNTextInput, Platform, Dimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { msStyles } from './KfaAddOverlay.styles';
import { useTheme } from '../../../../theme/useTheme';

export interface MSOption {
  id: string | number;
  displayText: string;
}

export interface MultiSelectDropdownProps {
  label?: string;
  options: MSOption[];
  selected: MSOption[];
  onChange: (selected: MSOption[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
}

// Each option row: paddingVertical:10*2 + fontSize:14 lineHeight ~22 + border:1 ≈ 43px
const OPTION_HEIGHT = 43;
// Show exactly 3 rows; rest scroll
const LIST_MAX_HEIGHT = OPTION_HEIGHT * 3;
// Search box height (paddingVertical:6*2 + input ~30) ≈ 42px
const SEARCH_BOX_HEIGHT = 42;
const MENU_APPROX_HEIGHT = SEARCH_BOX_HEIGHT + LIST_MAX_HEIGHT + 8; // 8 for borders/shadow

type MenuPosition = { top?: number; bottom?: number; left: number; width: number };

const KfaMultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label, options, selected, onChange, placeholder = 'Select...', singleSelect = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [menuPos, setMenuPos] = React.useState<MenuPosition>({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<any>(null);

  const toggle = () => {
    if (!open && triggerRef.current) {
      const screenH = Dimensions.get('window').height;
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
          const spaceBelow = screenH - rect.bottom;
          if (spaceBelow < MENU_APPROX_HEIGHT + 8) {
            setMenuPos({ bottom: screenH - (rect.top + scrollY), left: rect.left, width: rect.width });
          } else {
            setMenuPos({ top: rect.bottom + scrollY, left: rect.left, width: rect.width });
          }
        } catch (_) { /* ignore */ }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          const spaceBelow = screenH - (y + h);
          if (spaceBelow < MENU_APPROX_HEIGHT + 8) {
            setMenuPos({ bottom: screenH - y, left: x, width: w });
          } else {
            setMenuPos({ top: y + h, left: x, width: w });
          }
        });
      }
    }
    setOpen((o) => !o);
  };

  const isSelected = (item: MSOption) => selected.some((s) => String(s.id) === String(item.id));
  const handlePress = (item: MSOption) => {
    if (singleSelect) {
      onChange([item]);
      setOpen(false);
      setSearch('');
      return;
    }
    if (isSelected(item)) onChange(selected.filter((s) => String(s.id) !== String(item.id)));
    else onChange([...selected, item]);
  };
  const filtered = options.filter((o) => o.displayText.toLowerCase().includes(search.toLowerCase()));
  const displayText = selected.length === 0 ? placeholder : selected.map((s) => s.displayText).join(', ');

  const resolvedPosition: any = Platform.OS === 'web'
    ? {
        position: 'fixed',
        left: menuPos.left,
        width: menuPos.width,
        ...(menuPos.bottom !== undefined ? { bottom: menuPos.bottom } : { top: menuPos.top }),
      }
    : {
        position: 'absolute',
        left: menuPos.left,
        width: menuPos.width,
        ...(menuPos.bottom !== undefined ? { bottom: menuPos.bottom } : { top: menuPos.top }),
      };

  return (
    <View style={msStyles.container}>
      {label ? <Text style={[msStyles.label, isDark && { color: colors.text.secondary }]}>{label}</Text> : null}
      <TouchableOpacity
        ref={triggerRef}
        style={[
          msStyles.trigger,
          isDark && {
            backgroundColor: colors.input.background,
            borderColor: colors.input.border,
          },
        ]}
        onPress={toggle}
        activeOpacity={0.8}
      >
        <Text
          style={[
            msStyles.triggerText,
            selected.length === 0 && msStyles.placeholder,
            isDark && {
              color: selected.length === 0 ? colors.text.disabled : colors.text.primary,
            },
          ]}
          numberOfLines={1}
        >
          {displayText}
        </Text>
        <MaterialIcons
          name={open ? 'arrow-drop-up' : 'arrow-drop-down'}
          size={20}
          color={isDark ? colors.text.secondary : '#495057'}
        />
      </TouchableOpacity>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={msStyles.backdrop} activeOpacity={1} onPress={() => setOpen(false)} />
        <View
          style={[
            msStyles.menu,
            resolvedPosition,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
          ]}
        >
          <View
            style={[
              msStyles.searchBox,
              isDark && {
                backgroundColor: colors.input.background,
                borderBottomColor: colors.border.divider,
              },
            ]}
          >
            <MaterialIcons
              name="search"
              size={16}
              color={isDark ? colors.text.secondary : '#888'}
              style={{ marginRight: 6 }}
            />
            <RNTextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search..."
              placeholderTextColor={isDark ? colors.text.disabled : '#AAAAAA'}
              style={[
                msStyles.searchInput,
                isDark && {
                  color: colors.text.primary,
                },
              ]}
            />
          </View>
          <FlatList
            data={filtered}
            keyExtractor={(item) => String(item.id)}
            style={{ maxHeight: LIST_MAX_HEIGHT }}
            scrollEnabled
            keyboardShouldPersistTaps="always"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[
                  msStyles.option,
                  isSelected(item) && msStyles.optionSelected,
                  isDark && {
                    borderBottomColor: colors.border.divider,
                  },
                  isDark && isSelected(item) && {
                    backgroundColor: 'rgba(61,139,255,0.22)',
                  },
                ]}
                onPress={() => handlePress(item)}
              >
                <Text
                  style={[
                    msStyles.optionText,
                    isDark && {
                      color: colors.text.primary,
                    },
                  ]}
                >
                  {item.displayText}
                </Text>
                {isSelected(item) && (
                  <MaterialIcons
                    name="check"
                    size={18}
                    color={isDark ? colors.brand.primary : '#0066CC'}
                  />
                )}
              </TouchableOpacity>
            )}
          />
        </View>
      </Modal>
    </View>
  );
};

export default KfaMultiSelectDropdown;
